import {
  FileTypes,
  ReportDetailsDocument,
  ReportDetailsQuery,
  useAddAndRemoveFilesMutation,
} from '@/__generated__/graphql';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Box, Typography } from '@mui/material';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { downloadFile, urlToBase64 } from '@/utils';
import { useCallback, useEffect, useState } from 'react';
import * as state from '@/components/Analysis/state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { Image } from '@/components/Analysis/shared/Image';
import { useAtomValue } from 'jotai';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import Svg from 'react-inlinesvg';

const thumbnailBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  aspectRatio: '1',
  borderRadius: '8px',
  border: '1px solid rgba(0, 0, 0, 0.50)',
} as const;

type Props = {
  images: ReportDetailsQuery['report']['files'];
};

const ReportImage = ({
  file,
  setAttachments = () => {},
}: {
  file: Attachment;
  setAttachments?: React.Dispatch<React.SetStateAction<Attachment[]>>;
}) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (hasError) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    [hasError]
  );

  const handleError = useCallback(
    (success: boolean) => {
      setHasError(!success);

      if (!success) {
        setAttachments((previous) => {
          return previous.filter((attachment) => attachment.name !== file.name);
        });
      }
    },
    [file.name, setAttachments]
  );
  return (
    <>
      <Box sx={thumbnailBoxStyles}>
        <Image
          onClick={handleClick}
          src={file.url ?? ''}
          sx={{
            width: '100%',
            cursor: hasError ? 'not-allowed' : 'pointer',
            pointerEvents: hasError ? 'all' : 'auto',
          }}
          alt={file.name}
          onLoadEnd={handleError}
        />
      </Box>
      <Typography sx={{ textAlign: 'center', marginTop: '1rem' }}>{file.name || ''}</Typography>
    </>
  );
};

const PdfThumbnail = ({ file }: { file: Attachment }) => {
  const handleClick = () => {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  };

  return (
    <>
      <Box onClick={handleClick} sx={{ ...thumbnailBoxStyles, cursor: 'pointer' }}>
        <Svg
          style={{
            width: '25%',
            height: '25%',
          }}
          src="/assets/pdf_logo.svg"
        />
      </Box>
      <Typography sx={{ textAlign: 'center', marginTop: '1rem' }}>{file.name || ''}</Typography>
    </>
  );
};

export const ReportImagesGallery = ({ images }: Props) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [validAttachments, setValidAttachments] = useState<Attachment[]>(attachments ?? []);
  const [removeImageFromReport] = useAddAndRemoveFilesMutation({
    refetchQueries: [ReportDetailsDocument],
  });
  const { showSnackBar } = useSnackBarMessage({});
  const report = useAtomValue(state.selectedReport);

  useEffect(() => {
    setValidAttachments(attachments);
  }, [attachments]);

  useEffect(() => {
    if (!images || images.length === 0) {
      setAttachments([]);
      return;
    }

    let cancelled = false;

    const loadAttachments = async () => {
      try {
        const response: Attachment[] = await Promise.all(
          images.map(async ({ fileName, resourcePath, type, id }) => {
            const poiAttachmentUrl = getCloudfrontUrl(resourcePath);
            const isImage =
              type?.includes('image/') || [FileTypes.Jpeg, FileTypes.Png].includes(type);

            let base64: string | undefined;

            try {
              base64 = isImage ? await urlToBase64(poiAttachmentUrl) : undefined;
            } catch (error) {
              console.error(error);
              base64 = undefined;
            }

            return {
              id: id || '',
              name: fileName,
              base64,
              type,
              isNew: false,
              url: poiAttachmentUrl,
            };
          })
        );

        if (!cancelled) {
          setAttachments(response);
        }
      } catch (error) {
        console.error(error);
      }
    };

    loadAttachments();

    return () => {
      cancelled = true;
    };
  }, [images]);

  const handleRemoveAttachment = async (index: number) => {
    const fileId = attachments[index]?.id;

    if (!fileId) {
      showSnackBar('Attachment ID not found', 'error');
      return;
    }

    try {
      await removeImageFromReport({
        variables: {
          input: {
            collectionName: 'report',
            referenceId: report?.id || '',
            addFiles: [],
            removeFileIds: [fileId],
          },
        },
      });
    } catch (error) {
      const errorMessage = extractErrorMessageFromUnknownError(error);
      showSnackBar(errorMessage, 'error');
    }
  };

  const handleDownloadAttachment = (index: number) => {
    const currentAttachment = attachments[index];
    if (!currentAttachment.url) return;
    downloadFile(currentAttachment.url, currentAttachment.name);
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          width: '100%',
        }}
      >
        <MediaViewer
          mediaFiles={validAttachments}
          handleRemoveAttachment={handleRemoveAttachment}
          handleDownloadAttachment={handleDownloadAttachment}
        >
          {attachments?.map((file) => (
            <Box
              key={file.id}
              sx={{
                flex: '0 0 auto',
                width: '20%',
                margin: 1,
                boxSizing: 'border-box',
              }}
            >
              {file.type === 'PDF' ? (
                <PdfThumbnail file={file} />
              ) : (
                <ReportImage file={file} setAttachments={setValidAttachments} />
              )}
            </Box>
          ))}
        </MediaViewer>
      </Box>
    </Box>
  );
};
